import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../shared/design-system/structured-json-display.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import {
  InsuranceApiService,
  InsurancePolicyOption,
  InsuranceRegulatoryReportDefinitionDetail,
  InsuranceRegulatoryReportJobDetail,
  InsuranceRegulatoryValidationRule,
  InsuranceSanhabSubmission,
  PutInsuranceRegulatoryValidationRulesPayload
} from './insurance-api.service';
import {
  REGULATORY_PAYLOAD_FIELDS,
  REGULATORY_VALIDATION_RESULT_SCALAR_FIELDS,
  expandRegulatoryPayloadJson,
  flattenRegulatoryPayloadJson,
  parseRegulatoryValidationIssues
} from './insurance-regulatory-schemas';

type RegulatoryTab = 'summary' | 'rules' | 'job' | 'sanhab';

@Component({
  standalone: true,
  selector: 'ain-insurance-regulatory-job-detail',
  imports: [
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    CheckboxModule,
    SelectModule,
    TableModule,
    ButtonModule,
    DialogModule,
    StructuredJsonFormComponent,
    StructuredJsonDisplayComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.regulatory.console.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.regulatory.help.detail.title'"
        [descriptionKey]="'insurance.regulatory.help.detail.description'"
        [bulletKeys]="[
          'insurance.regulatory.help.detail.bullets.validate',
          'insurance.regulatory.help.detail.bullets.submit',
          'insurance.regulatory.help.detail.bullets.tabs'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!job()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/regulatory/jobs']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            <button pButton type="button" [label]="t('insurance.regulatory.console.actions.validate')" [disabled]="saving()" (click)="validateJob()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.regulatory.console.actions.submit')" [disabled]="saving() || !readyToSubmit" (click)="submitJob()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.regulatory.console.actions.download')" [disabled]="saving()" (click)="downloadPayload()"></button>
          </div>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.regulatory.console.tabs.summary') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.regulatory.help.summary.title'"
              [descriptionKey]="'insurance.regulatory.help.summary.description'"
              [bulletKeys]="[
                'insurance.regulatory.help.summary.bullets.definition',
                'insurance.regulatory.help.summary.bullets.period',
                'insurance.regulatory.help.summary.bullets.status'
              ]" />
          </div>
          <div class="summary">
            <span>{{ t('insurance.regulatory.console.fields.code') }}: <strong>{{ job()!.definitionCode }}</strong></span>
            <span>{{ t('columns.status') }}: <strong>{{ t(statusKey(job()!.status)) }}</strong></span>
            <span>{{ t('insurance.regulatory.console.fields.jurisdiction') }}: <strong>{{ job()!.jurisdiction }}</strong></span>
            <span>{{ t('insurance.regulatory.console.fields.periodFrom') }}: <strong>{{ job()!.periodFrom | ainDate }}</strong></span>
            <span>{{ t('insurance.regulatory.console.fields.periodTo') }}: <strong>{{ job()!.periodTo | ainDate }}</strong></span>
            <span>{{ t('insurance.regulatory.console.fields.submittedAt') }}: <strong>{{ job()!.submittedAtUtc ? (job()!.submittedAtUtc! | ainDate) : '-' }}</strong></span>
          </div>
          @if (!readyToSubmit) {
            <p class="field-hint">{{ t('insurance.regulatory.console.messages.validateBeforeSubmit') }}</p>
          }
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'summary'" (click)="activeTab.set('summary')">{{ t('insurance.regulatory.console.tabs.summary') }}</button>
            <button type="button" [class.active]="activeTab() === 'rules'" (click)="activeTab.set('rules')">{{ t('insurance.regulatory.console.tabs.rules') }}</button>
            <button type="button" [class.active]="activeTab() === 'job'" (click)="activeTab.set('job')">{{ t('insurance.regulatory.console.tabs.job') }}</button>
            <button type="button" [class.active]="activeTab() === 'sanhab'" (click)="activeTab.set('sanhab')">{{ t('insurance.regulatory.console.tabs.sanhab') }}</button>
          </div>

          @if (activeTab() === 'summary') {
            <div class="lines-header">
              <h4>{{ t('insurance.regulatory.console.tabs.summary') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.regulatory.help.tabs.summary.title'"
                [descriptionKey]="'insurance.regulatory.help.tabs.summary.description'"
                [bulletKeys]="[
                  'insurance.regulatory.help.tabs.summary.bullets.definition',
                  'insurance.regulatory.help.tabs.summary.bullets.validation'
                ]" />
            </div>
            <div class="summary-grid">
              <p><strong>{{ t('insurance.regulatory.console.fields.code') }}:</strong> {{ definition()?.code }}</p>
              <p><strong>{{ t('insurance.regulatory.console.fields.name') }}:</strong> {{ definition()?.name }}</p>
              <p><strong>{{ t('insurance.regulatory.console.fields.jurisdiction') }}:</strong> {{ definition()?.jurisdiction }}</p>
              <p><strong>{{ t('insurance.regulatory.console.fields.schemaVersion') }}:</strong> {{ definition()?.schemaVersion }}</p>
              <p><strong>{{ t('insurance.regulatory.console.fields.payloadFileId') }}:</strong> {{ job()!.payloadFileId || '-' }}</p>
              <p><strong>{{ t('insurance.regulatory.console.fields.lastError') }}:</strong> {{ job()!.lastError || '-' }}</p>
            </div>
            <div class="wide section-gap">
              <span class="field-label">{{ t('insurance.regulatory.console.fields.validationResult') }}</span>
              <ain-structured-json-display
                [fields]="validationResultScalarFields"
                [payloadJson]="validationResultJson()" />
              @if (validationIssues().length === 0) {
                <p class="field-hint">{{ t('insurance.regulatory.schemas.validation.noIssues') }}</p>
              } @else {
                <p-table [value]="validationIssues()" styleClass="lines-table section-gap">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('insurance.regulatory.console.fields.ruleCode') }}</th>
                      <th>{{ t('insurance.regulatory.console.fields.errorCode') }}</th>
                      <th>{{ t('insurance.regulatory.console.fields.errorMessage') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.ruleCode }}</td>
                      <td>{{ row.errorCode }}</td>
                      <td>{{ row.message }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            </div>
          } @else if (activeTab() === 'rules') {
            <div class="lines-header">
              <h4>{{ t('insurance.regulatory.console.tabs.rules') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.regulatory.help.tabs.rules.title'"
                  [descriptionKey]="'insurance.regulatory.help.tabs.rules.description'"
                  [bulletKeys]="[
                    'insurance.regulatory.help.tabs.rules.bullets.fields',
                    'insurance.regulatory.help.tabs.rules.bullets.save'
                  ]" />
                <button pButton type="button" [label]="t('common.add')" (click)="openRuleCreate()"></button>
                <button pButton type="button" severity="secondary" [label]="t('common.save')" [disabled]="saving()" (click)="saveRules()"></button>
              </div>
            </div>
            @if (rules().length === 0) {
              <p>{{ t('insurance.regulatory.console.messages.noRules') }}</p>
            } @else {
              <p-table [value]="rules()" dataKey="id" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.regulatory.console.fields.ruleCode') }}</th>
                    <th>{{ t('insurance.regulatory.console.fields.fieldPath') }}</th>
                    <th>{{ t('insurance.regulatory.console.fields.errorCode') }}</th>
                    <th>{{ t('insurance.regulatory.console.fields.required') }}</th>
                    <th>{{ t('insurance.regulatory.console.fields.active') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                  <tr>
                    <td>{{ row.ruleCode }}</td>
                    <td>{{ row.fieldPath }}</td>
                    <td>{{ row.errorCode }}</td>
                    <td>{{ row.isRequired ? t('common.yes') : t('common.no') }}</td>
                    <td>{{ row.isActive ? t('common.yes') : t('common.no') }}</td>
                    <td class="actions-cell">
                      <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openRuleEdit(rowIndex)"></button>
                      <button pButton type="button" text="true" severity="danger" [label]="t('common.delete')" (click)="removeRule(rowIndex)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
          } @else if (activeTab() === 'job') {
            <div class="lines-header">
              <h4>{{ t('insurance.regulatory.console.tabs.job') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.regulatory.help.tabs.job.title'"
                [descriptionKey]="'insurance.regulatory.help.tabs.job.description'"
                [bulletKeys]="[
                  'insurance.regulatory.help.tabs.job.bullets.payload',
                  'insurance.regulatory.help.tabs.job.bullets.validate'
                ]" />
            </div>
            <div class="wide">
              <span class="field-label">{{ t('insurance.regulatory.console.fields.payloadJson') }}</span>
              <ain-structured-json-form
                [fields]="payloadFields"
                [(payloadJson)]="payloadFlatJson" />
            </div>
            <div class="wide section-gap">
              <span class="field-label">{{ t('insurance.regulatory.console.fields.validationResult') }}</span>
              <ain-structured-json-display
                [fields]="validationResultScalarFields"
                [payloadJson]="validationResultJson()" />
              @if (validationIssues().length > 0) {
                <p-table [value]="validationIssues()" styleClass="lines-table">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('insurance.regulatory.console.fields.ruleCode') }}</th>
                      <th>{{ t('insurance.regulatory.console.fields.errorCode') }}</th>
                      <th>{{ t('insurance.regulatory.console.fields.errorMessage') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.ruleCode }}</td>
                      <td>{{ row.errorCode }}</td>
                      <td>{{ row.message }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            </div>
          } @else {
            <div class="lines-header">
              <h4>{{ t('insurance.regulatory.console.tabs.sanhab') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.regulatory.help.tabs.sanhab.title'"
                [descriptionKey]="'insurance.regulatory.help.tabs.sanhab.description'"
                [bulletKeys]="[
                  'insurance.regulatory.help.tabs.sanhab.bullets.policy',
                  'insurance.regulatory.help.tabs.sanhab.bullets.sync'
                ]" />
            </div>
            <div class="lifecycle-actions">
              <label>
                <span>{{ t('insurance.regulatory.console.fields.policy') }}</span>
                <p-select
                  [options]="policySelectOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="policyId"
                  name="policyId"
                  [filter]="true"
                  [showClear]="true"
                  appendTo="body" />
              </label>
              <button pButton type="button" [label]="t('insurance.regulatory.console.actions.syncPolicy')" [disabled]="saving() || !policyId" (click)="syncPolicy()"></button>
            </div>
            @if (sanhabSubmission()) {
              <div class="summary-grid section-gap">
                <p><strong>{{ t('insurance.regulatory.console.fields.externalReference') }}:</strong> {{ sanhabSubmission()!.externalReference }}</p>
                <p><strong>{{ t('columns.status') }}:</strong> {{ sanhabSubmission()!.status }}</p>
                <p><strong>{{ t('insurance.regulatory.console.fields.hash') }}:</strong> {{ sanhabSubmission()!.requestPayloadHash }}</p>
                <p><strong>{{ t('insurance.regulatory.console.fields.submittedAt') }}:</strong> {{ sanhabSubmission()!.createdAtUtc | ainDate }}</p>
              </div>
            } @else {
              <p>{{ t('insurance.regulatory.console.messages.noSubmission') }}</p>
            }
          }
        </section>

        @if (error()) { <p class="error">{{ error() }}</p> }
      }

      <p-dialog [header]="ruleDialogTitle()" [modal]="true" [(visible)]="ruleDialogVisible" [style]="{ width: '720px' }">
        <div class="dialog-grid">
          <label><span>{{ t('insurance.regulatory.console.fields.ruleCode') }}</span><input pInputText [(ngModel)]="ruleDraft.ruleCode" name="dialogRuleCode" /></label>
          <label><span>{{ t('insurance.regulatory.console.fields.fieldPath') }}</span><input pInputText [(ngModel)]="ruleDraft.fieldPath" name="dialogFieldPath" /></label>
          <label><span>{{ t('insurance.regulatory.console.fields.errorCode') }}</span><input pInputText [(ngModel)]="ruleDraft.errorCode" name="dialogErrorCode" /></label>
          <label class="wide"><span>{{ t('insurance.regulatory.console.fields.errorMessage') }}</span><input pInputText [(ngModel)]="ruleDraft.errorMessage" name="dialogErrorMessage" /></label>
          <label class="checkbox"><p-checkbox [binary]="true" [(ngModel)]="ruleDraft.isRequired" name="dialogIsRequired" /><span>{{ t('insurance.regulatory.console.fields.required') }}</span></label>
          <label class="checkbox"><p-checkbox [binary]="true" [(ngModel)]="ruleDraft.isActive" name="dialogIsActive" /><span>{{ t('insurance.regulatory.console.fields.active') }}</span></label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="ruleDialogVisible.set(false)"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="!ruleDraft.ruleCode.trim()" (click)="saveRuleDialog()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3 { margin: 0; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; font-size: 14px; }
    .lifecycle-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: end; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 10px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 10px; }
    .lines-header h4 { margin: 0; }
    .lines-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .section-gap { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
    .summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    .wide { grid-column: 1 / -1; }
    .field-label { display: block; color: #475569; font-size: 12px; font-weight: 600; margin-bottom: 6px; }
    .field-hint { font-size: 12px; color: #64748b; margin-top: 8px; }
    .checkbox { display: flex; gap: 8px; align-items: center; }
    .actions-cell { display: flex; gap: 4px; justify-content: flex-end; white-space: nowrap; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) {
      .summary-grid, .dialog-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsuranceRegulatoryJobDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly activeTab = signal<RegulatoryTab>('summary');
  readonly job = signal<InsuranceRegulatoryReportJobDetail | null>(null);
  readonly definition = signal<InsuranceRegulatoryReportDefinitionDetail | null>(null);
  readonly rules = signal<InsuranceRegulatoryValidationRule[]>([]);
  readonly sanhabSubmission = signal<InsuranceSanhabSubmission | null>(null);
  readonly policyOptions = signal<InsurancePolicyOption[]>([]);
  readonly error = signal('');
  readonly validationResultJson = signal('{}');
  readonly validationIssues = computed(() => parseRegulatoryValidationIssues(this.validationResultJson()));
  readonly ruleDialogVisible = signal(false);
  readonly payloadFields = REGULATORY_PAYLOAD_FIELDS;
  readonly validationResultScalarFields = REGULATORY_VALIDATION_RESULT_SCALAR_FIELDS;
  payloadFlatJson = '{}';
  policyId: string | null = null;
  readyToSubmit = false;
  ruleDraft: InsuranceRegulatoryValidationRule = this.emptyRuleDraft();
  private ruleDialogMode: 'create' | 'edit' = 'create';
  private ruleDialogIndex = -1;

  ngOnInit(): void {
    this.api.listPolicyOptions().subscribe({
      next: items => this.policyOptions.set(items),
      error: () => this.policyOptions.set([])
    });
    this.load();
  }

  pageTitle(): string {
    const current = this.job();
    if (!current) {
      return this.t('insurance.regulatory.console.title');
    }
    return `${current.definitionCode} — ${this.t(this.statusKey(current.status))}`;
  }

  policySelectOptions(): Array<{ label: string; value: string }> {
    return this.policyOptions().map(item => ({
      value: item.id,
      label: item.policyNumber
    }));
  }

  ruleDialogTitle(): string {
    return this.ruleDialogMode === 'create'
      ? this.t('insurance.regulatory.console.rules.add')
      : this.t('insurance.regulatory.console.rules.edit');
  }

  load(): void {
    const jobId = this.route.snapshot.paramMap.get('jobId');
    if (!jobId) {
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    this.api.getRegulatoryJob(jobId).subscribe({
      next: job => {
        this.job.set(job);
        this.setValidationResult(job?.validationResultJson ?? '{}');
        this.readyToSubmit = job?.status === 'Ready';
        this.payloadFlatJson = '{}';
        if (!job) {
          this.loading.set(false);
          return;
        }

        this.api.getRegulatoryDefinition(job.definitionId).subscribe({
          next: definition => {
            this.definition.set(definition);
            if (definition) {
              this.payloadFlatJson = flattenRegulatoryPayloadJson(JSON.stringify({
                definitionCode: definition.code,
                jurisdiction: definition.jurisdiction,
                schemaVersion: definition.schemaVersion,
                data: { plate: '', nationalId: '' }
              }));
            }
            this.api.listRegulatoryValidationRules(job.definitionId).subscribe({
              next: rules => {
                this.rules.set(rules.map(item => ({ ...item })));
                this.loading.set(false);
              },
              error: () => {
                this.rules.set([]);
                this.loading.set(false);
              }
            });
          },
          error: () => {
            this.definition.set(null);
            this.rules.set([]);
            this.loading.set(false);
          }
        });
      },
      error: () => {
        this.job.set(null);
        this.loading.set(false);
      }
    });
  }

  private setValidationResult(json: string): void {
    this.validationResultJson.set(json || '{}');
  }

  openRuleCreate(): void {
    this.ruleDialogMode = 'create';
    this.ruleDialogIndex = -1;
    this.ruleDraft = this.emptyRuleDraft();
    this.ruleDialogVisible.set(true);
  }

  openRuleEdit(index: number): void {
    const row = this.rules()[index];
    if (!row) {
      return;
    }
    this.ruleDialogMode = 'edit';
    this.ruleDialogIndex = index;
    this.ruleDraft = { ...row };
    this.ruleDialogVisible.set(true);
  }

  saveRuleDialog(): void {
    const next = [...this.rules()];
    const draft = { ...this.ruleDraft };
    if (this.ruleDialogMode === 'edit' && this.ruleDialogIndex >= 0) {
      next[this.ruleDialogIndex] = draft;
    } else {
      next.push(draft);
    }
    this.rules.set(next);
    this.ruleDialogVisible.set(false);
  }

  removeRule(index: number): void {
    this.rules.set(this.rules().filter((_, itemIndex) => itemIndex !== index));
  }

  saveRules(): void {
    const definition = this.definition();
    if (!definition) {
      return;
    }
    const payload: PutInsuranceRegulatoryValidationRulesPayload = {
      items: this.rules().map(item => ({
        ruleCode: item.ruleCode,
        fieldPath: item.fieldPath,
        isRequired: item.isRequired,
        errorCode: item.errorCode,
        errorMessage: item.errorMessage,
        isActive: item.isActive
      }))
    };
    this.saving.set(true);
    this.api.putRegulatoryValidationRules(definition.id, payload).subscribe({
      next: items => {
        this.rules.set(items);
        this.saving.set(false);
      },
      error: () => {
        this.error.set(this.t('insurance.states.loadError'));
        this.saving.set(false);
      }
    });
  }

  validateJob(): void {
    const job = this.job();
    if (!job || !confirm(this.t('insurance.regulatory.console.messages.validateConfirm'))) {
      return;
    }
    const expandedPayload = expandRegulatoryPayloadJson(this.payloadFlatJson);
    const hasPayload = expandedPayload !== '{}';
    this.saving.set(true);
    this.api.validateRegulatoryJob(job.id, { payloadJson: hasPayload ? expandedPayload : null }).subscribe({
      next: result => {
        this.setValidationResult(JSON.stringify({
          isValid: result.isValid,
          issues: result.issues.map(item => ({
            ruleCode: item.ruleCode,
            errorCode: item.errorCode,
            message: item.message
          }))
        }, null, 2));
        this.readyToSubmit = result.isValid;
        this.load();
        this.saving.set(false);
      },
      error: () => {
        this.error.set(this.t('insurance.regulatory.console.messages.validateFailed'));
        this.saving.set(false);
      }
    });
  }

  submitJob(): void {
    const job = this.job();
    if (!job || !this.readyToSubmit || !confirm(this.t('insurance.regulatory.console.messages.submitConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.api.submitRegulatoryJob(job.id, { force: false }).subscribe({
      next: () => {
        this.load();
        this.saving.set(false);
      },
      error: () => {
        this.error.set(this.t('insurance.regulatory.console.messages.jobNotReady'));
        this.saving.set(false);
      }
    });
  }

  downloadPayload(): void {
    const job = this.job();
    if (!job) {
      return;
    }
    this.saving.set(true);
    this.api.downloadRegulatoryJob(job.id).subscribe({
      next: result => {
        const raw = atob(result.contentBase64);
        this.payloadFlatJson = flattenRegulatoryPayloadJson(raw);
        this.saving.set(false);
      },
      error: () => {
        this.error.set(this.t('insurance.states.loadError'));
        this.saving.set(false);
      }
    });
  }

  syncPolicy(): void {
    const value = this.policyId?.trim();
    if (!value || !confirm(this.t('insurance.regulatory.console.messages.syncConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.api.syncSanhabPolicy(value, { force: false, payloadJson: null }).subscribe({
      next: submission => {
        this.sanhabSubmission.set(submission);
        this.saving.set(false);
      },
      error: () => {
        this.error.set(this.t('insurance.states.loadError'));
        this.saving.set(false);
      }
    });
  }

  private emptyRuleDraft(): InsuranceRegulatoryValidationRule {
    return {
      id: '',
      definitionId: this.job()?.definitionId ?? '',
      ruleCode: '',
      fieldPath: '',
      isRequired: true,
      errorCode: '',
      errorMessage: '',
      isActive: true
    };
  }

  statusKey(status: string): string {
    return `insurance.regulatory.console.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
